import React, { useRef } from 'react';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { LoadingIndicator } from './loading-indicator';
import messages from './messages';

type Props = {
	isLoadingPrevious?: boolean;
	isLoadingNext?: boolean;
	onLoadingThresholdCheck?: (scrollElement: {
		clientHeight: number;
		scrollHeight: number;
		scrollTop: number;
	}) => void;
};

export const LoadingManager = ({ isLoadingPrevious, isLoadingNext }: Props) => {
	const { formatMessage } = useIntl();

	const prevIsLoadingPrevious = usePreviousWithInitial(isLoadingPrevious);
	const hasFinishedLoadingPrevious = prevIsLoadingPrevious && !isLoadingPrevious;

	const prevIsLoadingNext = usePreviousWithInitial(isLoadingNext);
	const hasFinishedLoadingNext = prevIsLoadingNext && !isLoadingNext;

	const loadingCompleteLiveMessage = useRef<MessageDescriptor | undefined>(undefined);
	if (isLoadingPrevious) {
		loadingCompleteLiveMessage.current = messages.loadStartA11YMessageTop;
	} else if (hasFinishedLoadingPrevious) {
		loadingCompleteLiveMessage.current = messages.loadEndA11YMessageTop;
	} else if (isLoadingNext) {
		loadingCompleteLiveMessage.current = messages.loadStartA11YMessageBottom;
	} else if (hasFinishedLoadingNext) {
		loadingCompleteLiveMessage.current = messages.loadEndA11YMessageBottom;
	}

	return (
		<>
			{isLoadingPrevious && <LoadingIndicator position="top" />}
			{isLoadingNext && <LoadingIndicator position="bottom" />}
			{loadingCompleteLiveMessage.current && (
				<ScreenReaderText role="alert" aria-live="polite">
					{formatMessage(loadingCompleteLiveMessage.current)}
				</ScreenReaderText>
			)}
		</>
	);
};
